import Image from "next/image";
import tamiLogoGreen from "@/assets/svgs/tami-square-simple-green.svg";
import { hidesPrice, resolveCtaLabel } from "@/lib/plans/price-on-request";
import { formatCurrency } from "@/lib/utils";
import { Plan, PLAN_CYCLE } from "@/services/plans/types";
import { FeatureItem } from "./feature-item";
import { PlanInquiryModal } from "@/components/plan-inquiry/plan-inquiry-modal";
import { PriceOnRequestBlock } from "./price-on-request-block";
import { TamiButtonPrimary } from "@/components/tami-button";
import { HOME_COPY } from "../home.copy";

/**
 * O quadro de Collab não tem mais piso de 5 assentos — começa com o dono mais
 * 1 creator e cresce daí. Dois avatares (dono + creator) junto do texto
 * "cresce quando precisar" comunicam isso sem prometer um mínimo que não
 * existe. O preço fica no `EntryPrice`: a base é da Collab e cada creator
 * soma o adicional.
 */
const SeatsPreview = () => (
  <div className="flex items-center gap-1.5">
    {[0, 1].map((index) => (
      <span
        key={index}
        className="h-7 w-7 rounded-full border-2 border-tami-light/30 bg-tami-light/10"
        aria-hidden
      />
    ))}
    <span className="ml-1 text-sm text-tami-light/70">
      {HOME_COPY.enterpriseCard.seatsPreview}
    </span>
  </div>
);

interface EntryPriceProps {
  plan: Plan;
}

/**
 * O número grande é o pacote base, que é da própria Collab; cada criador soma
 * o adicional, inclusive o primeiro. Por isso o rótulo não é "a partir de" —
 * ninguém assina só a base — e o adicional vem logo abaixo, para não virar
 * surpresa no checkout.
 *
 * Quando `hidesPrice` diz que não há preço para anunciar — por decisão
 * comercial ou por dado incompleto — a gente não inventa número nenhum:
 * preferimos "fale com a gente" a um R$ 0,00 que o checkout não cumpre.
 */
const EntryPrice = ({ plan }: EntryPriceProps) => {
  const isAnnual = plan.cycle === PLAN_CYCLE.ANNUAL;

  if (hidesPrice(plan)) {
    return (
      <PriceOnRequestBlock
        plan={plan}
        description={HOME_COPY.enterpriseCard.onRequestDescription}
      />
    );
  }

  const [integer = "0", decimal = "00"] = formatCurrency(
    plan.baseValueNumber as number,
  )
    .replace(/R\$\s*/, "")
    .trim()
    .split(",");

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-bold uppercase tracking-[0.09em] text-tami-green">
        {HOME_COPY.enterpriseCard.packageLabel}
      </span>

      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-medium text-tami-light">R$</span>
        <span className="text-5xl font-bold leading-none text-tami-light">
          {integer}
        </span>
        <span className="-ml-1 self-start text-2xl font-bold text-tami-light">
          ,{decimal}
        </span>
        <span className="ml-1 text-lg text-tami-light">
          {isAnnual
            ? HOME_COPY.enterpriseCard.perYear
            : HOME_COPY.enterpriseCard.perMonth}
        </span>
      </div>

      <p className="text-sm leading-snug text-tami-light/70">
        {HOME_COPY.enterpriseCard.perCreator(
          formatCurrency(plan.additionalUnitValueNumber as number),
        )}
      </p>
    </div>
  );
};

interface EnterprisePricingCardProps {
  plan: Plan;
}

export const EnterprisePricingCard = ({ plan }: EnterprisePricingCardProps) => {
  // Sem preço publicado, o CTA não pode levar ao checkout — o visitante iria
  // parar numa cobrança que a página não sabe informar. Os dois botões saem
  // de `/sign-up` nesse estado, mas o destino divergente depende do motivo:
  // sob consulta (decisão comercial) abre o formulário, que a API aceita;
  // dado incompleto (defesa) volta pro WhatsApp, porque a API rejeita com 404
  // qualquer plano sem `priceOnRequest` — abrir o mesmo formulário ali seria
  // um funil que nunca completa.
  const isPriceHidden = hidesPrice(plan);
  const ctaLabel = resolveCtaLabel(plan);

  const copy = HOME_COPY.enterpriseCard;

  return (
    <div className="flex h-full flex-col lg:mx-4">
      {/* Card Principal — borda verde separa a conta de Collab da conta individual */}
      <div className="relative flex flex-1 flex-col overflow-hidden rounded-[40px] border-2 border-tami-green bg-tami-dark p-6 shadow-[0_4px_8px_theme(colors.tami.dark/30%)]">
        {/* Header com badge e logo */}
        <div className="mb-6 flex items-start justify-between">
          <div className="rounded-full bg-tami-green px-4 py-2">
            <span className="text-sm font-bold text-tami-dark md:text-base">
              {copy.badge}
            </span>
          </div>

          <div className="flex-shrink-0">
            <Image
              src={tamiLogoGreen}
              alt={copy.logoAlt}
              width={40}
              height={40}
              className="object-contain"
            />
          </div>
        </div>

        {/* Seção Superior - Identidade à esquerda, preço e CTA à direita */}
        <div className="mb-7 grid grid-cols-1 gap-8 md:grid-cols-[1.35fr_1fr]">
          <div>
            <h3 className="mb-4 text-left text-3xl font-bold text-tami-light md:text-4xl">
              {plan.name}
            </h3>
            <p className="max-w-[520px] text-sm text-tami-light">
              {copy.intro}
            </p>
            <div className="mt-5">
              <SeatsPreview />
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <EntryPrice plan={plan} />
            {plan.priceOnRequest ? (
              <PlanInquiryModal
                plan={plan}
                ctaLabel={ctaLabel}
                className="font-bold"
              />
            ) : isPriceHidden ? (
              <TamiButtonPrimary
                href="https://wa.me/5581982676567"
                className="font-bold"
              >
                {ctaLabel}
              </TamiButtonPrimary>
            ) : (
              <TamiButtonPrimary href="/sign-up" className="font-bold">
                {copy.ctaTalk}
              </TamiButtonPrimary>
            )}
          </div>
        </div>

        {/* Features em 3 colunas */}
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 lg:grid-cols-3">
          {copy.features.map((feature) => (
            <FeatureItem
              key={feature.title}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>

      {/* Botão CTA - Fora do card mas parte do componente */}
      <div className="mt-4">
        {plan.priceOnRequest ? (
          <PlanInquiryModal plan={plan} ctaLabel={ctaLabel} className="w-full">
            {copy.bottomCta.before}{" "}
            <span className="font-bold">{copy.bottomCta.bold}</span>
          </PlanInquiryModal>
        ) : isPriceHidden ? (
          <TamiButtonPrimary
            href="https://wa.me/5581982676567"
            className="w-full"
          >
            {copy.bottomCta.before}{" "}
            <span className="font-bold">{copy.bottomCta.bold}</span>
          </TamiButtonPrimary>
        ) : (
          <TamiButtonPrimary href="/sign-up" className="w-full">
            {copy.bottomCtaSignUp.before}{" "}
            <span className="font-bold">{copy.bottomCtaSignUp.bold}</span>
          </TamiButtonPrimary>
        )}
      </div>
    </div>
  );
};
